function buildInvoiceHtml(invoice, clinicSettings, includeControls = true) {
  const logo = clinicSettings?.clinic_logo_url || '';
  const clinicName = clinicSettings?.clinic_name || '1Clin';
  const clinicAddr = clinicSettings?.clinic_address || '';
  const clinicPhone = clinicSettings?.clinic_phone || '';
  const gstNum = clinicSettings?.gst_number || '';
  const tagline = clinicSettings?.tagline || 'Healthcare Simplified, Care Amplified.';

  // Generate logo HTML - use image if available, otherwise text logo
  const logoHtml = logo 
    ? `<img src="${logo}" alt="${clinicName}" />`
    : `<div style="width:56px;height:56px;border-radius:12px;background:linear-gradient(135deg,#14b8a6,#0f766e);display:flex;align-items:center;justify-content:center;color:white;font-family:'Manrope',sans-serif;font-weight:700;font-size:20px;">1C</div>`;

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Invoice ${invoice.invoice_number}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700&family=DM+Sans:wght@400;500&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'DM Sans', sans-serif; color: #1e293b; background: #fff; padding: 40px; max-width: 800px; margin: 0 auto; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #0f766e; }
  .logo-section { display: flex; align-items: center; gap: 14px; }
  .logo-section img { width: 56px; height: 56px; border-radius: 12px; object-fit: contain; }
  .clinic-name { font-family: 'Manrope', sans-serif; font-size: 22px; font-weight: 700; color: #0f766e; }
  .tagline { font-size: 10px; color: #94a3b8; margin-top: 2px; }
  .clinic-info { font-size: 12px; color: #64748b; margin-top: 4px; }
  .invoice-meta { text-align: right; }
  .invoice-meta h2 { font-family: 'Manrope', sans-serif; font-size: 18px; font-weight: 700; color: #1e293b; }
  .invoice-meta p { font-size: 12px; color: #64748b; margin-top: 3px; }
  .patient-section { background: #f8fafc; border-radius: 8px; padding: 16px; margin-bottom: 24px; }
  .patient-section h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; margin-bottom: 6px; }
  .patient-section p { font-size: 14px; font-weight: 500; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  th { background: #0f766e; color: white; padding: 10px 14px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; }
  th:last-child { text-align: right; }
  td { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
  td:last-child { text-align: right; font-weight: 500; }
  .totals { float: right; width: 280px; }
  .totals .row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
  .totals .row.total { border-top: 2px solid #0f766e; padding-top: 10px; margin-top: 6px; font-weight: 700; font-size: 16px; }
  .footer { clear: both; margin-top: 60px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 11px; color: #94a3b8; }
  .status-badge { display: inline-block; padding: 3px 10px; border-radius: 99px; font-size: 11px; font-weight: 600; }
  .status-paid { background: #d1fae5; color: #065f46; }
  .status-pending { background: #fef3c7; color: #92400e; }
  @media print { body { padding: 20px; } .no-print { display: none; } }
</style></head>
<body>
  ${includeControls ? `<div class="no-print" style="margin-bottom:20px;text-align:right">
    <button onclick="window.print()" style="background:#0f766e;color:white;border:none;padding:10px 24px;border-radius:8px;cursor:pointer;font-size:14px;font-weight:600">
      Print
    </button>
  </div>` : ''}
  <div class="header">
    <div class="logo-section">
      ${logoHtml}
      <div>
        <div class="clinic-name">${clinicName}</div>
        <div class="tagline">${tagline}</div>
        ${clinicAddr ? `<div class="clinic-info">${clinicAddr}</div>` : ''}
        ${clinicPhone ? `<div class="clinic-info">Phone: ${clinicPhone}</div>` : ''}
        ${gstNum ? `<div class="clinic-info">GSTIN: ${gstNum}</div>` : ''}
      </div>
    </div>
    <div class="invoice-meta">
      <h2>INVOICE</h2>
      <p>${invoice.invoice_number}</p>
      <p>Date: ${invoice.date}</p>
      <p>Payment: ${invoice.payment_method?.toUpperCase()}</p>
      <p><span class="status-badge ${invoice.payment_status === 'paid' ? 'status-paid' : 'status-pending'}">${invoice.payment_status?.toUpperCase()}</span></p>
    </div>
  </div>
  <div class="patient-section">
    <h3>Bill To</h3>
    <p>${invoice.patient_name}</p>
  </div>
  <table>
    <thead><tr><th>#</th><th>Description</th><th>Amount (\u20B9)</th></tr></thead>
    <tbody>
      ${(invoice.items || []).map((item, i) => `<tr><td>${i + 1}</td><td>${item.description}</td><td>\u20B9${item.amount?.toFixed(2)}</td></tr>`).join('')}
    </tbody>
  </table>
  <div class="totals">
    <div class="row"><span>Subtotal</span><span>\u20B9${invoice.subtotal?.toFixed(2)}</span></div>
    ${invoice.gst_percent > 0 ? `<div class="row"><span>GST (${invoice.gst_percent}%)</span><span>\u20B9${invoice.gst_amount?.toFixed(2)}</span></div>` : ''}
    <div class="row total"><span>Total</span><span>\u20B9${invoice.total?.toFixed(2)}</span></div>
  </div>
  <div class="footer">
    <p>Thank you for visiting ${clinicName}</p>
    <p style="margin-top:4px">${tagline}</p>
  </div>
</body></html>`;
}

export function openInvoicePrint(invoice, clinicSettings) {
  const html = buildInvoiceHtml(invoice, clinicSettings, true);
  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

export async function downloadInvoicePdf(invoice, clinicSettings) {
  const html = buildInvoiceHtml(invoice, clinicSettings, false);
  const html2pdf = (await import('html2pdf.js')).default;
  const filename = `Invoice_${(invoice.invoice_number || invoice.patient_name || 'Bill').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  await html2pdf().from(html, 'string').set({
    margin: 10,
    filename,
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
  }).save();
}

// WhatsApp share function for invoice
export function shareInvoiceWhatsApp(invoice, clinicSettings, patientPhone = '') {
  const clinicName = clinicSettings?.clinic_name || '1Clin';
  
  let message = `*${clinicName} - Invoice*\n\n`;
  message += `Invoice No: ${invoice.invoice_number}\n`;
  message += `Date: ${invoice.date}\n`;
  message += `Patient: ${invoice.patient_name}\n\n`;
  
  message += `*Items:*\n`;
  (invoice.items || []).forEach((item, i) => {
    message += `${i+1}. ${item.description} - \u20B9${item.amount?.toFixed(2)}\n`;
  });
  
  message += `\n*Subtotal:* \u20B9${invoice.subtotal?.toFixed(2)}\n`;
  if (invoice.gst_percent > 0) {
    message += `*GST (${invoice.gst_percent}%):* \u20B9${invoice.gst_amount?.toFixed(2)}\n`;
  }
  message += `*Total:* \u20B9${invoice.total?.toFixed(2)}\n`;
  message += `*Status:* ${invoice.payment_status?.toUpperCase()}\n`;
  
  message += `\n_Thank you for visiting ${clinicName}_`;
  
  const phoneNumber = patientPhone?.replace(/[^0-9]/g, '') || '';
  const whatsappUrl = phoneNumber 
    ? `https://wa.me/91${phoneNumber}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;
  
  window.open(whatsappUrl, '_blank');
}
